import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Send, Lightbulb, CircleDot, Clock } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../AuthContext";
import { useLang } from "../i18n";

const STATUS_STEP = { inviata: 1, in_revisione: 2, in_sviluppo: 3, pubblicata: 4 };
const PIPELINE = ["inviata", "in_revisione", "in_sviluppo", "pubblicata"];

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [ideas, setIdeas] = useState([]);
  const [form, setForm] = useState({ title: "", category: "giochi", platform: "iOS + Android", description: "" });
  const [busy, setBusy] = useState(false);

  const loadIdeas = () => api.get("/ideas/mine").then(({ data }) => setIdeas(data)).catch(() => {});
  useEffect(() => { loadIdeas(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/ideas", form);
      toast.success(t.toastIdea);
      setForm({ title: "", category: "giochi", platform: "iOS + Android", description: "" });
      loadIdeas();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080A0C] text-gray-100 terminal-grid">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-20">
        <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="dashboard-header">
          <p className="font-mono text-xs tracking-[0.3em] text-[#00FF66] mb-3">{t.dashTag}</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-white">{t.hello}<span className="neon-text">{user?.name}</span></h1>
          <p className="text-gray-400 mt-2 text-sm sm:text-base">{t.dashSub}</p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 mt-12">
          {/* Form */}
          <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="glass-card rounded-2xl p-7 h-fit">
            <h2 className="font-display font-semibold text-lg text-white flex items-center gap-2 mb-6">
              <Lightbulb size={18} className="text-[#00FF66]" /> {t.formTitle}
            </h2>
            <form onSubmit={submit} className="space-y-4">
              <input data-testid="idea-title-input" className="gc-input" placeholder={t.phIdeaTitle} value={form.title} onChange={set("title")} required minLength={3} />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="idea-category-select" className="gc-input" value={form.category} onChange={set("category")}>
                  <option value="giochi">{t.cats.giochi}</option>
                  <option value="produttivita">{t.cats.produttivita}</option>
                  <option value="utility">{t.cats.utility}</option>
                </select>
                <select data-testid="idea-platform-select" className="gc-input" value={form.platform} onChange={set("platform")}>
                  <option>iOS + Android</option>
                  <option>iOS</option>
                  <option>Android</option>
                </select>
              </div>
              <textarea data-testid="idea-description-input" className="gc-input min-h-[130px] resize-y" placeholder={t.phIdeaDesc} value={form.description} onChange={set("description")} required minLength={10} />
              <button data-testid="idea-submit-btn" type="submit" disabled={busy} className="btn-neon w-full py-3.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <Send size={14} /> {busy ? t.sending : t.btnSendIdea}
              </button>
            </form>
          </motion.div>

          {/* Ideas list */}
          <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}>
            <h2 className="font-display font-semibold text-lg text-white mb-5">{t.yourIdeas} <span className="font-mono text-xs text-gray-500">({ideas.length})</span></h2>
            {ideas.length === 0 ? (
              <div data-testid="ideas-empty-state" className="glass-card rounded-2xl p-10 text-center">
                <CircleDot size={28} className="text-[#1F2937] mx-auto mb-4" />
                <p className="font-mono text-sm text-gray-500">{t.emptyIdeas}</p>
                <p className="text-xs text-gray-600 mt-2">{t.emptySub}</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="ideas-list">
                <AnimatePresence>
                  {ideas.map((idea) => {
                    const step = STATUS_STEP[idea.status] || 1;
                    const lastNote = [...(idea.status_history || [])].reverse().find((h) => h.note);
                    return (
                      <motion.div key={idea.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                        data-testid={`idea-card-${idea.id}`}
                        className="glass-card glow-border rounded-2xl p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-display font-semibold text-white">{idea.title}</h3>
                            <p className="font-mono text-[10px] text-gray-500 mt-1 tracking-wider uppercase">{t.cats[idea.category] || idea.category} · {idea.platform}</p>
                          </div>
                          <span data-testid={`idea-status-${idea.id}`} className={`font-mono text-[10px] tracking-widest px-3 py-1.5 rounded-full border uppercase badge-${idea.status}`}>{t.statuses[idea.status]}</span>
                        </div>
                        <p className="text-sm text-gray-400 mt-3 leading-relaxed">{idea.description}</p>
                        {lastNote && (
                          <div data-testid={`idea-note-${idea.id}`} className="mt-4 border-l-2 border-[#00FF66] bg-[#0D1117]/70 rounded-r-lg px-4 py-3">
                            <p className="font-mono text-[9px] tracking-widest text-[#00FF66]/80 uppercase">{t.noteFromTeam} · {t.statuses[lastNote.status]}</p>
                            <p className="text-sm text-gray-300 mt-1.5">{lastNote.note}</p>
                          </div>
                        )}
                        <div className="mt-5">
                          <div className="flex items-center gap-1.5">
                            {PIPELINE.map((s, i) => (
                              <div key={s} className="flex-1 h-1 rounded-full transition-colors" style={{ background: i < step ? "#00FF66" : "#1F2937" }} />
                            ))}
                          </div>
                          <div className="flex justify-between mt-2 font-mono text-[9px] text-gray-600 tracking-wider uppercase">
                            {PIPELINE.map((s) => <span key={s} className={STATUS_STEP[s] <= step ? "text-[#00FF66]/80" : ""}>{t.statuses[s]}</span>)}
                          </div>
                        </div>
                        <p className="flex items-center gap-1.5 font-mono text-[10px] text-gray-600 mt-4">
                          <Clock size={10} /> {new Date(idea.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}
                        </p>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
